import type { FiuuTopUpStart, FiuuTopUpState, LegacyList } from '@mypoz/shared';
import { findFiuuTopUpState, startFiuuTopUp } from '@mypoz/shared';

import { legacyApi, topUpApi } from '@/lib/legacy-api.js';

/** Raised when `get_trade` answers with anything but its list. The vendor's text is for the log. */
export class WalletRequestError extends Error {
  constructor(
    readonly endpoint: string,
    readonly code: string,
    message: string,
  ) {
    super(`${endpoint} failed (${code}): ${message}`);
    this.name = 'WalletRequestError';
  }
}

/** Well above the 573 rows the test account has; a full page means ask again with more room. */
const TOP_UP_PAGE_CEILING = 1000;

/**
 * Every top-up on the account, newest first — `get_trade` with `type=CHARGE` (MYP-107).
 *
 * Fetched whole, not page by page: the server cannot filter by status, and the summary's
 * "Top-ups" figure counts the completed ones across all of them. One request returned all 553 live
 * (2026-10-01). If a page ever comes back full, the account has outgrown the ceiling and is asked
 * once more with ten times the room, the same way `order_list` is handled.
 */
export async function fetchTopUpRows(): Promise<readonly unknown[]> {
  return fetchTrades('CHARGE');
}

/**
 * Every movement on the account — `get_trade` with `type=ALL` (MYP-146), for the transaction
 * history. Fetched whole for the same reason as the top-ups: only rows the vendor marks `END` are
 * shown (`normaliseTrades`), and the server cannot filter by status, so the browser does.
 */
export async function fetchTradeRows(): Promise<readonly unknown[]> {
  return fetchTrades('ALL');
}

async function fetchTrades(type: 'CHARGE' | 'ALL'): Promise<readonly unknown[]> {
  const first = await callTrades(type, TOP_UP_PAGE_CEILING);
  return first.length < TOP_UP_PAGE_CEILING ? first : callTrades(type, TOP_UP_PAGE_CEILING * 10);
}

async function callTrades(type: 'CHARGE' | 'ALL', limit: number): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('get_trade', {
    type,
    page: '1',
    limit: String(limit),
  });
  if (!result.ok) throw new WalletRequestError('get_trade', result.code, result.message);
  return result.data.list;
}

/**
 * Start a top-up of `amountCents` (MYP-134): `molpay_pay` records it as waiting for payment and
 * answers with the link to the vendor's gateway page, already checked to be that page and for this
 * amount. Throws `FiuuTopUpError` (from `@mypoz/shared`) otherwise — its `kind` says what the
 * screen should tell the customer.
 *
 * Every call records a new top-up, so it is called once per press of Pay. Nothing calls it while
 * `FIUU_TOPUP_ENABLED` is off.
 */
export function createFiuuTopUp(amountCents: number): Promise<FiuuTopUpStart> {
  return startFiuuTopUp(topUpApi, amountCents);
}

/** The newest top-ups only: one just started is the first row, so a short page is enough. */
const RECENT_TOP_UPS = 20;

/**
 * How one top-up stands (MYP-134), read from `get_trade` — the vendor has no status endpoint of its
 * own. Looked up by its record id, or its top-up number when that is all the address carried.
 * Undefined when it is not among the customer's newest top-ups; throws `WalletRequestError` when
 * `get_trade` gives no answer.
 */
export async function fetchFiuuTopUpState(wanted: {
  readonly accdetailid?: string;
  readonly orderid?: string;
}): Promise<FiuuTopUpState | undefined> {
  return findFiuuTopUpState(await callTrades('CHARGE', RECENT_TOP_UPS), wanted);
}
